import React, { useState, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import Column from '@amzn/meridian/column'
import Loader from '@amzn/meridian/loader'
import Text from '@amzn/meridian/text'
import Box from '@amzn/meridian/box'
import { getStoreId, getNotificationStoreId, getStoreList as getStoreListFromRedux } from '@atredux/selectors/storeSelector'
import { NotificationAPIService } from './services/NotificationAPIService'
import {
  INotificationData,
  IIndividualNotification,
  INotificationsPageState,
  IFilteredNotifications,
} from './types'
import { filterNotificationsByStores, separateNotificationsByCurrentStore } from './utils'
import { StoreFilterSelect, ClearFilterButton, StoreFilterSheet } from './components'
import { TabToggle } from './components/TabToggle'
import { ViewOnlyNotice } from './components/ViewOnlyNotice'
import { EmptyState } from './components/EmptyState'
import { AggregatedNotificationGroup } from './components/AggregatedNotificationGroup'
import { IndividualNotificationGroup } from './components/IndividualNotificationGroup'
import { LOADING_NOTIFICATIONS, UNABLE_TO_LOAD_ERROR, ALL_STORES, STORE_SELECTION_SUMMARY } from './constants'
import { useBundle } from '@amzn/react-arb-tools'
import { BUNDLE_NAME, NO_CURRENT_STORE } from '@src/constants'
import { t } from '@src/translationHelper'
import './NotificationsPage.scss'

const isValidNotification = (notification: IIndividualNotification): boolean => {
    return !!(
    notification.notificationMessage &&
    notification.timestamp &&
    notification.notificationAction &&
    notification.deepUrl
  )
}

export const NotificationsPage: React.FC = () => {
    const rawStoreId = useSelector(getStoreId)
    const hasCurrentStore = !!rawStoreId && rawStoreId !== NO_CURRENT_STORE
    const currentStore = hasCurrentStore ? rawStoreId : undefined
    const notificationStoreId = useSelector(getNotificationStoreId)
    const reduxStoreList = useSelector(getStoreListFromRedux)
    const [bundle] = useBundle(BUNDLE_NAME)
    const otherStoreIds = useMemo(
        () => reduxStoreList.map((s) => s.storeId).filter((id) => id && id !== notificationStoreId),
        [reduxStoreList, notificationStoreId],
    )

    const [state, setState] = useState<INotificationsPageState>({
        notificationData: null,
        selectedStoreIds: [],
        isFilterSheetOpen: false,
        activeTab: hasCurrentStore ? 'current-store' : 'all-other-stores',
        isLoading: false,
        error: null,
    })

    /**
     * Fetches notification data and filters out invalid notifications.
     * Iterates through the nested structure (notifications → toolNotifications → allIndividualNotifications)
     * and removes any individual notification missing required fields (message, timestamp, action, deepUrl).
     * Preserves the same data structure while ensuring data quality.
     */
    const loadNotificationData = async (cancelled: () => boolean) => {
        if (!notificationStoreId) {
            setState((prev) => ({ ...prev, isLoading: false, notificationData: null, error: null }))
            return
        }
        setState((prev) => ({ ...prev, isLoading: true, error: null }))
        try {
            const data = await NotificationAPIService.getNotifications(notificationStoreId, otherStoreIds)
            if (cancelled()) return
            const validatedData: INotificationData = {
                notifications: data.notifications.map((storeNotif) => ({
                    ...storeNotif,
                    toolNotifications: storeNotif.toolNotifications.map((toolNotif) => ({
                        ...toolNotif,
                        allIndividualNotifications: toolNotif.allIndividualNotifications.filter(isValidNotification),
                    })),
                })),
            }
            setState((prev) => ({ ...prev, notificationData: validatedData, isLoading: false }))
        } catch (error) {
            if (cancelled()) return
            console.error('Failed to load notifications:', error)
            setState((prev) => ({ ...prev, error: UNABLE_TO_LOAD_ERROR, isLoading: false }))
        }
    }

    const handleApplyFilter = (storeIds: string[]) => {
        setState((prev) => ({ ...prev, selectedStoreIds: storeIds }))
    }

    const handleClearFilter = () => {
        setState((prev) => ({ ...prev, selectedStoreIds: [] }))
    }

    const handleOpenFilterSheet = () => {
        setState((prev) => ({ ...prev, isFilterSheetOpen: true }))
    }

    const handleCloseFilterSheet = () => {
        setState((prev) => ({ ...prev, isFilterSheetOpen: false }))
    }

    const handleTabChange = (tab: 'all-other-stores' | 'current-store') => {
        setState((prev) => ({ ...prev, activeTab: tab }))
    }

    const getFilteredNotifications = (): IFilteredNotifications => {
        if (!state.notificationData || !notificationStoreId) {
            return { currentStore: [], otherStores: [] }
        }

        const filteredByStore = filterNotificationsByStores(state.notificationData, state.selectedStoreIds)

        // When no scanned store, treat all notifications as "other stores" (aggregated only)
        const separationStoreId = currentStore || ''
        const { otherStoreNotifications } =
            separateNotificationsByCurrentStore(filteredByStore, separationStoreId)
        const { currentStoreNotifications: allCurrentStoreNotifications } =
            separateNotificationsByCurrentStore(state.notificationData, separationStoreId)
        if (state.activeTab === 'all-other-stores') {
            let aggregated: IFilteredNotifications['otherStores']

            // Filter is active: iterate over selectedStoreIds to include stores with zero notifications
            if (state.selectedStoreIds.length > 0) {
                // Include the current store if it's in the selected filter stores to show its aggregated notification
                aggregated = state.selectedStoreIds.map((filteredStoreId) => {
                    // Check if this store has notifications in the filtered store data
                    const match = filteredByStore.notifications.find((s) => s.storeId === filteredStoreId)
                    if (match) {
                        return {
                            storeId: match.storeId,
                            storeName: getStoreName(match.storeId),
                            toolName: match.toolNotifications[0]?.toolName || '',
                            aggregatedNotifications: match.toolNotifications.map((toolNotif) => toolNotif.aggregatedNotification),
                        }
                    }
                    // No matching notifications — render with zero notifications
                    return {
                        storeId: filteredStoreId,
                        storeName: getStoreName(filteredStoreId),
                        toolName: '',
                        aggregatedNotifications: [],
                    }
                })
            } else { // No filter active: render only stores with notifications
                aggregated = otherStoreNotifications.map((storeNotif) => ({
                    storeId: storeNotif.storeId,
                    storeName: getStoreName(storeNotif.storeId),
                    toolName: storeNotif.toolNotifications[0]?.toolName || '',
                    aggregatedNotifications: storeNotif.toolNotifications.map((toolNotif) => toolNotif.aggregatedNotification),
                }))
            }
            return { currentStore: [], otherStores: aggregated.sort((a, b) => (a.storeName || '').localeCompare(b.storeName || '')) }
        } else { // The active tab shows current store notifications
            const individual = allCurrentStoreNotifications.map((storeNotif) => ({
                storeId: storeNotif.storeId,
                storeName: getStoreName(storeNotif.storeId),
                toolName: storeNotif.toolNotifications[0]?.toolName || '',
                notifications: storeNotif.toolNotifications
                    .flatMap((toolNotif) => toolNotif.allIndividualNotifications)
                    .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime()),
            }))
            return { currentStore: individual, otherStores: [] }
        }
    }

    /**
     * Calculates notification counts for badges in the tabs.
     * - All Other Stores: counts tool notifications (aggregated) from filtered stores
     * - Current Store: counts individual notifications (unfiltered) from current store only
     */
    const calculateTabCounts = (): { allOtherStoresCount: number; currentStoreCount: number } => {
        if (!state.notificationData || !notificationStoreId) {
            return { allOtherStoresCount: 0, currentStoreCount: 0 }
        }
        const separationStoreId = currentStore || ''
        // Apply store filter for "All Other Stores" count
        const filteredByStore = filterNotificationsByStores(state.notificationData, state.selectedStoreIds)
        const { otherStoreNotifications } =
            separateNotificationsByCurrentStore(filteredByStore, separationStoreId)
        // Count pending tasks across all aggregated notifications for other stores
        const allOtherStoresCount = otherStoreNotifications.reduce(
            (total, storeNotif) => total + storeNotif.toolNotifications.reduce(
                (toolTotal, toolNotif) => toolTotal + toolNotif.aggregatedNotification.numPendingTasks, 0), 0)

        const { currentStoreNotifications: allCurrentStoreNotifications } =
            separateNotificationsByCurrentStore(state.notificationData, separationStoreId)
        // Count individual notifications (flatten all tools)
        const currentStoreCount = allCurrentStoreNotifications.reduce(
            (total, storeNotif) => total + storeNotif.toolNotifications.reduce(
                (toolTotal, toolNotif) => toolTotal + toolNotif.allIndividualNotifications.length, 0), 0)
        return { allOtherStoresCount, currentStoreCount }
    }

    useEffect(() => {
        let cancelled = false
        loadNotificationData(() => cancelled)
        return () => { cancelled = true }
    }, [notificationStoreId, otherStoreIds])

    const getStoreList = () => {
        return reduxStoreList
    }

    const getStoreName = (storeId: string): string => {
        const store = reduxStoreList.find((s) => s.storeId === storeId)
        return store?.storeName || storeId
    }

    const getEmptyStateContext = (): 'no-notifications' | 'all-caught-up' => {
        if (state.selectedStoreIds.length > 0) return 'no-notifications'
        return 'all-caught-up'
    }

    const filteredNotifications = getFilteredNotifications()
    const tabCounts = calculateTabCounts()
    const storeList = getStoreList()
    const filterDisplayText = state.selectedStoreIds.length === 0
        ? t(bundle, ALL_STORES)
        : t(bundle, STORE_SELECTION_SUMMARY, {
            selectedCount: state.selectedStoreIds.length,
            totalCount: reduxStoreList.length,
        })
    const shouldShowEmptyState =
        (state.activeTab === 'all-other-stores' && filteredNotifications.otherStores.length === 0 && state.selectedStoreIds.length === 0) ||
        (state.activeTab === 'current-store' && filteredNotifications.currentStore.length === 0)

    return (
        <Column height="100%" spacing="none">
            {state.isLoading && (
                <Column alignmentHorizontal="center" spacingInset="600 400">
                    <Loader />
                    <Text>{t(bundle, LOADING_NOTIFICATIONS)}</Text>
                </Column>
            )}
            {state.error && (
                <Column alignmentHorizontal="center" spacingInset="400">
                    <Box type="outline" width="100%" spacingInset="300 450">
                        <Text type="b300" color="error">{t(bundle, state.error)}</Text>
                    </Box>
                </Column>
            )}
            {!state.isLoading && !state.error && state.notificationData && (
                <div className="notifications-page-container">
                    <Column spacing="400">
                        <Column spacing="100">
                            <StoreFilterSelect
                                displayText={filterDisplayText}
                                onTap={handleOpenFilterSheet}
                                isOpen={state.isFilterSheetOpen}
                            />
                            <ClearFilterButton
                                visible={state.selectedStoreIds.length > 0}
                                onClear={handleClearFilter}
                            />
                        </Column>
                        <TabToggle
                            activeTab={state.activeTab}
                            allOtherStoresCount={tabCounts.allOtherStoresCount}
                            currentStoreCount={tabCounts.currentStoreCount}
                            onTabChange={handleTabChange}
                        />
                        <ViewOnlyNotice visible={state.activeTab === 'all-other-stores'} />
                        {shouldShowEmptyState && <EmptyState context={getEmptyStateContext()} />}
                        {state.activeTab === 'all-other-stores' && !shouldShowEmptyState && (
                            <>
                                {filteredNotifications.otherStores.map((storeGroup) => (
                                    <AggregatedNotificationGroup
                                        key={storeGroup.storeId}
                                        storeId={storeGroup.storeId}
                                        storeName={storeGroup.storeName}
                                        toolName={storeGroup.toolName}
                                        aggregatedNotifications={storeGroup.aggregatedNotifications}
                                    />
                                ))}
                            </>
                        )}
                        {state.activeTab === 'current-store' && !shouldShowEmptyState && (
                            <>
                                {filteredNotifications.currentStore.map((storeGroup) => (
                                    <IndividualNotificationGroup
                                        key={storeGroup.storeId}
                                        storeId={storeGroup.storeId}
                                        storeName={storeGroup.storeName}
                                        toolName={storeGroup.toolName}
                                        notifications={storeGroup.notifications}
                                    />
                                ))}
                            </>
                        )}
                        <StoreFilterSheet
                            open={state.isFilterSheetOpen}
                            onClose={handleCloseFilterSheet}
                            stores={storeList}
                            initialSelectedStoreIds={state.selectedStoreIds}
                            onApply={handleApplyFilter}
                        />
                    </Column>
                </div>
            )}
        </Column>
    )
}

export default NotificationsPage
